/**
 * Narrowing the survey column: the phrase in the search box, and the three facets behind the one
 * filter button beside it.
 *
 * ── One button, never a row of chips ─────────────────────────────────────────────────────────
 * Owner, 16 September 2026: „za dużo się dzieje". KANON `Raporty` draws a search field and a single
 * filter control over the list, and the facets live inside it. What that costs is a place to state
 * that a facet is on, which is why `activeFacetCount` exists — the button carries the figure, and a
 * narrowed list is never a list that looks empty for no reason.
 *
 * ── The search box searches a form OR a person ───────────────────────────────────────────────
 * The canon's own placeholder is „Szukaj ankiety lub klienta", so a phrase matches a survey's title
 * and the NAMES of the clients who have it. The names arrive as a parameter — a map from customer id
 * to the name the backend composed — because this module may not read a query, and because a rule
 * that fetched its own inputs could not be tested against a roster that has not loaded yet. A
 * missing map is a real state: the phrase then matches titles alone rather than matching nothing.
 *
 * ── What is NOT here, and why ────────────────────────────────────────────────────────────────
 * A facet on the ANSWER TYPE of a survey's questions. `GET /survey/templates` carries
 * `questionCount` and no items, so answering "which of my forms asks for a photograph" would mean a
 * `GET /survey/template/{id}` per row — sixteen requests for a list of fifteen, and sixteen loading
 * states on a screen whose whole policy is that nothing moves while it fills. It is a finding for
 * the contract, not something to fake here.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import type { SurveyCadence, SurveyGroups, SurveyRow } from './surveyList';

/** Whether a form is running. `all` is the absence of the facet, not a third status. */
export type StatusFacet = 'all' | 'active' | 'paused';

/** Which rhythm, or every rhythm. The intake has none and is never matched by a cadence facet. */
export type CadenceFacet = 'all' | SurveyCadence;

export interface SurveyFacets {
  /** As typed. Trimmed and folded when it is applied, never on the way in — the field shows it back. */
  readonly query: string;
  readonly status: StatusFacet;
  readonly cadence: CadenceFacet;
  /** One client's id, or `null` for every client. */
  readonly clientId: number | null;
}

export const NO_SURVEY_FACETS: SurveyFacets = {
  query: '',
  status: 'all',
  cadence: 'all',
  clientId: null,
};

/** How many of the three facets are narrowing the list. The phrase is not one of them — it is visible. */
export function activeFacetCount(facets: SurveyFacets): number {
  return (
    (facets.status === 'all' ? 0 : 1) +
    (facets.cadence === 'all' ? 0 : 1) +
    (facets.clientId === null ? 0 : 1)
  );
}

/**
 * WHICH of the two things the trainer set is hiding the cards.
 *
 * Three sentences rather than one, because „nic nie pasuje" beside a filter button carrying a figure
 * is the case where a trainer goes looking for a form that is right there. The phrase is visible in
 * the field and the facets are behind a closed popup, so the sentence has to name whichever of them
 * is responsible — or both.
 */
export function noResultsReason(
  facets: SurveyFacets,
): 'search' | 'filters' | 'searchAndFilters' {
  const phrase = facets.query.trim() !== '';
  const narrowed = activeFacetCount(facets) > 0;
  if (phrase && narrowed) return 'searchAndFilters';
  return phrase ? 'search' : 'filters';
}

/** Whether anything at all is narrowing the list — which is what separates „nic nie pasuje" from „pusto". */
export function surveyListIsNarrowed(facets: SurveyFacets): boolean {
  return facets.query.trim() !== '' || activeFacetCount(facets) > 0;
}

/**
 * Case- and accent-insensitive folding, so „cwiczenia" finds „Ćwiczenia".
 *
 * NFD then strip the combining marks: a Polish trainer typing without diacritics on a foreign
 * keyboard is the ordinary case, not the exotic one.
 */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();
}

function matchesQuery(
  row: SurveyRow,
  folded: string,
  clientNames: ReadonlyMap<number, string>,
): boolean {
  if (folded === '') return true;
  if (fold(row.title).includes(folded)) return true;
  return row.assignedCustomerIds.some((id) => {
    const name = clientNames.get(id);
    return name !== undefined && fold(name).includes(folded);
  });
}

function matchesFacets(row: SurveyRow, facets: SurveyFacets): boolean {
  if (facets.status === 'active' && !row.active) return false;
  if (facets.status === 'paused' && row.active) return false;
  if (facets.cadence !== 'all' && row.cadence !== facets.cadence) return false;
  // The client facet cannot be asked of the INTAKE, and the reason is on the wire: the backend fills
  // `assignedCustomerIds` from check-in enrolments, so the mandatory form names nobody at all while
  // its `assignedCount` is not zero. Testing membership in that list dropped the intake from the
  // list the moment any client was chosen — a form every client fills, hidden by a filter naming
  // one of them. It is exempt rather than matched, which is the same answer the footer's own cell
  // gives for the same reason.
  if (facets.clientId !== null && !row.mandatory && !row.assignedCustomerIds.includes(facets.clientId)) {
    return false;
  }
  return true;
}

/** Whether one row survives the phrase and the facets. */
function surveyMatches(
  row: SurveyRow,
  facets: SurveyFacets,
  clientNames: ReadonlyMap<number, string>,
): boolean {
  return matchesFacets(row, facets) && matchesQuery(row, fold(facets.query.trim()), clientNames);
}

/**
 * The column, narrowed — as two groups, because the groups are what is drawn.
 *
 * A narrowing that removes the intake removes the „Obowiązkowa" eyebrow with it rather than leaving
 * a heading over nothing, which is why the intake goes through the same predicate as everything
 * else: the form is mandatory for the CLIENT, not exempt from the trainer's own search.
 */
export function filterSurveyGroups(
  groups: SurveyGroups,
  facets: SurveyFacets,
  clientNames: ReadonlyMap<number, string>,
): SurveyGroups {
  return {
    intake:
      groups.intake !== null && surveyMatches(groups.intake, facets, clientNames)
        ? groups.intake
        : null,
    leadForm:
      groups.leadForm != null && surveyMatches(groups.leadForm, facets, clientNames)
        ? groups.leadForm
        : null,
    checkIns: groups.checkIns.filter((row) => surveyMatches(row, facets, clientNames)),
  };
}

/**
 * The clients the filter offers, in the order the menu lists them.
 *
 * Only the ones who actually hold a survey: a facet that can select a client no form is assigned to
 * is a control whose every use empties the list. Names come from the roster map, so a client the
 * roster has not answered for yet is simply not offered rather than offered as a blank line.
 *
 * The CHECK-INS are the whole source, and the intake is deliberately not asked. It names nobody on
 * the wire — `assignedCustomerIds` is filled from check-in enrolments and the intake does not
 * recur — so reading its list added nothing and reading it as truth was what made the facet look
 * like it had an opinion about the mandatory form. It does not: `matchesFacets` exempts that row, so
 * whoever is chosen here, the intake stays in the column.
 *
 * What this cannot do is offer a client whose ONLY form is the intake. The wire carries a COUNT for
 * it and no ids at all, so the names would have to be guessed from the roster — and „wstępną ma 7 z
 * 8" is the backend's own statement that the roster is not the answer. It joins the contract
 * findings in `AGENTS.md` rather than being invented here.
 */
export interface FacetClient {
  readonly id: number;
  readonly name: string;
}

export function facetClients(
  groups: SurveyGroups,
  clientNames: ReadonlyMap<number, string>,
  locale: string,
): readonly FacetClient[] {
  const ids = new Set<number>();
  for (const row of groups.checkIns) {
    for (const id of row.assignedCustomerIds) ids.add(id);
  }

  const collator = new Intl.Collator(locale);
  return [...ids]
    .map((id) => ({ id, name: clientNames.get(id) ?? '' }))
    .filter((client) => client.name !== '')
    .sort((left, right) => collator.compare(left.name, right.name));
}
